import imageCompression from 'browser-image-compression';
import React, { useEffect, useState } from 'react';

import DeleteIcon from '@mui/icons-material/Delete';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
} from '@mui/material';

import { getMainDomain } from '../utils/component-utils';
import { showTokenModal } from './TokenModal';

interface FileSelectorModalProps {
  open: boolean;
  onClose: () => void;
  onSelect: (fileUrl: string) => void;
  configParam: string;
  handleUpload: (blob: Blob, filename: string) => Promise<boolean>;
}

const FileSelectorModal: React.FC<FileSelectorModalProps> = ({
  open,
  onClose,
  onSelect,
  configParam,
  handleUpload,
}) => {
  const [files, setFiles] = useState<string[]>([]);
  const [newFile, setNewFile] = useState<File | null>(null);
  const [deletingFile, setDeletingFile] = useState<string | null>(null);

  useEffect(() => {
    const fetchFiles = async () => {
      if (configParam === 'localhost') {
        // For local development, use empty array
        setFiles([]);
        return;
      }
      const mainDomain = getMainDomain();

      const fetchUrl = `https://upload.${mainDomain}/files/${configParam}`;

      try {
        // Get auth token
        const authToken = await showTokenModal();
        if (!authToken) {
          console.log(
            'File listing cancelled - no authentication token provided'
          );
          return;
        }

        const response = await fetch(fetchUrl, {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
          method: 'GET',
          credentials: 'include',
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        setFiles(data); // Directly set the array of URLs
      } catch (error) {
        console.error('Error fetching files:', error);
      }
    };

    if (open) {
      fetchFiles();
    }
  }, [open, configParam]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setNewFile(event.target.files[0]);
    }
  };

  const compressAndUpload = async () => {
    if (newFile) {
      try {
        let blob: Blob;
        let fileName: string;

        // Check if file is an image and not a GIF (to preserve animations)
        const isImage = newFile.type.startsWith('image/');
        const isGif = newFile.type === 'image/gif';

        if (isImage && !isGif) {
          // Compress images except GIFs
          const options = {
            maxSizeMB: 1,
            maxWidthOrHeight: 1920,
            useWebWorker: true,
          };
          const compressedFile = await imageCompression(newFile, options);
          blob = new Blob([compressedFile], { type: compressedFile.type });
          fileName = compressedFile.name.replace(/ /g, '_');
        } else {
          // Don't compress GIFs, videos and other files
          blob = new Blob([newFile], { type: newFile.type });
          fileName = newFile.name.replace(/ /g, '_');
        }

        const success = await handleUpload(blob, fileName);

        if (success) {
          const fileUrl = `./site_data/${configParam}/${fileName}`;

          onSelect(fileUrl);
          setNewFile(null);
        } else {
          console.log('Upload cancelled or failed');
        }
      } catch (error) {
        console.error('Error processing file:', error);
      }
    }
  };

  const handleDeleteFile = async (fileUrl: string) => {
    if (configParam === 'localhost') {
      console.log('File deletion not supported in localhost mode');
      return;
    }

    try {
      setDeletingFile(fileUrl);

      // Get auth token
      const authToken = await showTokenModal();
      if (!authToken) {
        console.log(
          'File deletion cancelled - no authentication token provided'
        );
        setDeletingFile(null);
        return;
      }

      // Extract filename from URL
      const fileName = fileUrl.split('/').pop();
      const mainDomain = getMainDomain();
      const deleteUrl = `https://upload.${mainDomain}/upload/${configParam}/${fileName}`;

      const response = await fetch(deleteUrl, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Remove file from local state
      setFiles((prevFiles) => prevFiles.filter((file) => file !== fileUrl));
      console.log('File deleted successfully');
    } catch (error) {
      console.error('Error deleting file:', error);
    } finally {
      setDeletingFile(null);
    }
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Select or Upload Image</DialogTitle>
      <DialogContent>
        <List>
          {files.map((file, index) => (
            <ListItem
              key={index}
              component="div"
              secondaryAction={
                configParam !== 'localhost' && (
                  <IconButton
                    edge="end"
                    aria-label="delete"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteFile(file);
                    }}
                    disabled={deletingFile === file}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                )
              }
            >
              <ListItemButton onClick={() => onSelect(file)}>
                <img
                  src={'/site_data/' + file.split('/').slice(-2).join('/')}
                  alt={`thumbnail-${index}`}
                  style={{ width: '50px', height: '50px', marginRight: '10px' }}
                />
                <ListItemText primary={file.split('/').pop()} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
        <Box mt={2}>
          <input type="file" placeholder="file" onChange={handleFileChange} />
          <Button
            variant="contained"
            color="primary"
            onClick={compressAndUpload}
            disabled={!newFile}
          >
            Upload
          </Button>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
};

export default FileSelectorModal;
